import { bankersRound, resolveQuoteDiscount } from './quoteTotals'
import { includesGstFlag, resolvedCatalogItemSell, DEFAULT_STANDARD_MARGIN_PCT } from './productPricing'
import { isCalculatedQuoteTask, isTimeBasedQuoteTask } from './calculatedQuoteDisplay'

function lineQty (item) {
  return Number(item.qty) || 0
}

function lineSellUnit (item) {
  const base = Number(item.sell_price) || 0
  const callout = Number(item.callout_fee) || 0
  return base + callout
}

function parseItemDetails (item) {
  if (!item || item.item_details == null || item.item_details === '') return null
  if (typeof item.item_details === 'object') return item.item_details
  try {
    return JSON.parse(item.item_details)
  } catch (e) {
    return null
  }
}

function getTaskCalculations (item) {
  if (Array.isArray(item.calculations) && item.calculations.length > 0) {
    return item.calculations
  }
  const details = parseItemDetails(item)
  if (details && Array.isArray(details.calculations) && details.calculations.length > 0) {
    return details.calculations
  }
  return null
}

function forEachTaskMaterial (item, fn) {
  const materials = item && item.materials
  if (!materials) return
  if (Array.isArray(materials)) {
    materials.forEach(fn)
    return
  }
  if (typeof materials === 'object') {
    ;(materials.default || []).forEach(fn)
    ;(materials.company || []).forEach(fn)
  }
}

function forEachTaskVariableCost (item, fn) {
  const variables = item && item.variables
  if (!variables) return
  if (Array.isArray(variables)) {
    variables.forEach((v) => {
      if (v && (v.selected == null || v.selected)) fn(v)
    })
    return
  }
  if (typeof variables === 'object') {
    ;(variables.default || []).concat(variables.company || []).forEach(fn)
  }
}

/** Matches calculated task modal materialsBuyCost (per line qty). */
export function computeTaskMaterialBuyCostFromCalculations (calculations, qty = 1) {
  if (!Array.isArray(calculations) || !calculations.length) return 0
  let sum = 0
  calculations.forEach((calc) => {
    const mult = Number(calc.multiplier) || 0
    if (calc.material && calc.material.cost != null) {
      sum += (Number(calc.material.cost) || 0) * mult
    }
    ;(calc.fittings || []).forEach((f) => {
      if (Number(f.qty) > 0 && f.cost != null) {
        sum += (Number(f.cost) || 0) * Number(f.qty)
      }
    })
    ;(calc.fixings || []).forEach((f) => {
      if (Number(f.qty) > 0 && f.cost != null) {
        sum += (Number(f.cost) || 0) * Number(f.qty)
      }
    })
  })
  const q = Number(qty) || 1
  return bankersRound(sum * q, 2)
}

/** Material sell total from calculated task calculations (per line qty). */
export function computeTaskMaterialSellFromCalculations (calculations, qty = 1, standardMarginPct = DEFAULT_STANDARD_MARGIN_PCT) {
  if (!Array.isArray(calculations) || !calculations.length) return 0
  let sum = 0
  calculations.forEach((calc) => {
    const mult = Number(calc.multiplier) || 0
    if (calc.material) {
      sum += resolvedCatalogItemSell(calc.material, standardMarginPct) * mult
    }
    ;(calc.fittings || []).forEach((f) => {
      if (Number(f.qty) > 0) {
        sum += resolvedCatalogItemSell(f, standardMarginPct) * Number(f.qty)
      }
    })
    ;(calc.fixings || []).forEach((f) => {
      if (Number(f.qty) > 0) {
        sum += resolvedCatalogItemSell(f, standardMarginPct) * Number(f.qty)
      }
    })
  })
  const q = Number(qty) || 1
  return bankersRound(sum * q, 2)
}

function taskMaterialSellFromItem (item) {
  if (!item || item.item_type !== 'task') return 0
  const qty = lineQty(item) || 1
  let materialSell = 0

  forEachTaskMaterial(item, (m) => {
    const mQty = Number(m.qty) || 0
    if (mQty <= 0) return
    materialSell += (Number(m.sell_price) || 0) * mQty
  })

  forEachTaskVariableCost(item, (v) => {
    materialSell += Number(v.sell_price) || 0
  })

  const calculations = getTaskCalculations(item)
  if (calculations) {
    materialSell = computeTaskMaterialSellFromCalculations(calculations, qty)
  }

  if (materialSell > 0) {
    return bankersRound(materialSell, 2)
  }

  return 0
}

/** Material sell total for a quote task line (materials/variables only; no labour). */
export function quoteLineMaterialSellTotal (item) {
  return taskMaterialSellFromItem(item)
}

/** Gross margin on sell for materials: (sell − buy) / sell. */
export function productMarginPercent (materialSell, materialBuy) {
  return marginPercent(materialSell, materialBuy)
}

/** Markup on cost for materials: (sell − buy) / buy — matches account Product Markup %. */
export function productMarkupOnCostPercent (materialSell, materialBuy) {
  const buy = parseFloat(materialBuy)
  const sell = parseFloat(materialSell)
  if (!Number.isFinite(buy) || buy <= 0) return 0
  return bankersRound(((sell - buy) / buy) * 100, 1)
}

function storedLineCostTotal (item, qty) {
  const totalCost = Number(item.total_cost)
  if (Number.isFinite(totalCost) && totalCost > 0) {
    return bankersRound(totalCost, 2)
  }
  const unitCost = Number(item.unit_cost)
  if (Number.isFinite(unitCost) && unitCost > 0) {
    return bankersRound(unitCost * qty, 2)
  }
  const details = parseItemDetails(item)
  if (details && details.cost != null) {
    const detailCost = Number(details.cost)
    if (Number.isFinite(detailCost) && detailCost > 0) {
      return bankersRound(detailCost * qty, 2)
    }
  }
  return 0
}

/** Customer-facing line sell total (before quote-level discount) */
export function quoteLineSellTotal (item) {
  if (!item) return 0
  const qty = lineQty(item)
  if (item.item_type === 'task') {
    const unit = lineSellUnit(item)
    if (isTimeBasedQuoteTask(item) || isCalculatedQuoteTask(item)) {
      return unit
    }
    return unit * qty
  }
  if (item.item_type === 'product') {
    return (Number(item.sell_price) || 0) * qty
  }
  return 0
}

/** Direct material buy cost for a task line (materials/products only; no labour cost) */
export function quoteLineMaterialCostTotal (item) {
  if (!item || item.item_type !== 'task') return 0
  const qty = lineQty(item) || 1
  let materialCost = 0

  forEachTaskMaterial(item, (m) => {
    const mQty = Number(m.qty) || 0
    if (mQty <= 0) return
    materialCost += (Number(m.cost) || 0) * mQty
  })

  forEachTaskVariableCost(item, (v) => {
    materialCost += Number(v.cost) || 0
  })

  const calculations = getTaskCalculations(item)
  if (calculations) {
    materialCost = computeTaskMaterialBuyCostFromCalculations(calculations, qty)
  }

  if (materialCost > 0) {
    return bankersRound(materialCost, 2)
  }

  return storedLineCostTotal(item, qty)
}

/** Product line buy cost */
export function quoteLineProductCostTotal (item) {
  if (!item || item.item_type !== 'product') return 0
  const qty = lineQty(item)
  const unitCost = Number(item.cost) != null ? Number(item.cost) : (Number(item.unit_cost) || 0)
  if (unitCost > 0) {
    return bankersRound(unitCost * qty, 2)
  }
  return storedLineCostTotal(item, qty)
}

export function quoteLineCostTotal (item) {
  if (!item) return 0
  if (item.item_type === 'task') return quoteLineMaterialCostTotal(item)
  if (item.item_type === 'product') return quoteLineProductCostTotal(item)
  return 0
}

export function marginPercent (sellTotal, costTotal) {
  const sell = parseFloat(sellTotal)
  const cost = parseFloat(costTotal)
  if (!Number.isFinite(sell) || sell <= 0) return 0
  return bankersRound(((sell - cost) / sell) * 100, 1)
}

export function grossProfit (sellTotal, costTotal) {
  return bankersRound((parseFloat(sellTotal) || 0) - (parseFloat(costTotal) || 0), 2)
}

/**
 * Quote-level margin after discount.
 * Sell side uses post-discount subtotal; cost side is sum of line costs (undiscounted direct costs).
 */
export function calculateQuoteMargin (quote, items, discountAmount) {
  const quoteItems = Array.isArray(items) ? items : []
  let sellBeforeDiscount = 0
  let costTotal = 0

  quoteItems.forEach((it) => {
    sellBeforeDiscount += quoteLineSellTotal(it)
    costTotal += quoteLineCostTotal(it)
  })

  const quoteCallout = parseFloat(quote && quote.callout_fee)
  if (!isNaN(quoteCallout) && quoteCallout > 0) {
    sellBeforeDiscount += quoteCallout
  }

  const discount = discountAmount != null && discountAmount !== ''
    ? Number(discountAmount) || 0
    : resolveQuoteDiscount(sellBeforeDiscount, quote)
  const sellAfterDiscount = Math.max(0, sellBeforeDiscount - discount)
  const profit = grossProfit(sellAfterDiscount, costTotal)

  return {
    sell_total: bankersRound(sellAfterDiscount, 2),
    cost_total: bankersRound(costTotal, 2),
    gross_profit: profit,
    margin_pct: marginPercent(sellAfterDiscount, costTotal)
  }
}
